import classNames from 'classnames';
import isNil from 'lodash/isNil';
import React, { type JSX } from 'react';

import './HStack.less';

// ==============================
// TYPES
// ==============================
type HStackSharedProps = {
  className?: string;
  children: React.ReactNode;
};

/** Anonymous numeric gap (with optional block / align / justify) xor named preset — never both. */
type HStackProps =
  | (HStackSharedProps & {
    align?: 'center' | 'end' | 'top';
    /** Block-level flex row (full width). Default is inline-flex for mid-copy clusters. */
    block?: boolean;
    gap: 8 | 12 | 16;
    justify?: 'end' | 'space-between';
    // ----------
    preset?: never;
  })
  | (HStackSharedProps & {
    preset: 'page';
    // ----------
    align?: never;
    block?: never;
    gap?: never;
    justify?: never;
  });

// ==============================
// COMPONENTS
// ==============================
/** Horizontal flex row — inline-flex by default; pass `block` for a full-width row. */
function HStack(props: HStackProps): JSX.Element {
  const { align, block = false, className, children, gap, justify, preset } = props;
  return (
    <div
      className={classNames(
        'HStack',
        {
          'HStack--block': block,
          [`HStack--align-${align}`]: align,
          [`HStack--gap-${gap}`]: !isNil(gap),
          [`HStack--justify-${justify}`]: justify,
          [`HStack--preset-${preset}`]: !isNil(preset)
        },
        className
      )}
    >
      {children}
    </div>
  );
}

export { HStack };
export type { HStackProps };
